/**
 * LMSTrainerDashboardPage.jsx
 * Sprint LMS-X — Trainer Console.
 * Shows the courses owned/co-owned by this trainer, the learners enrolled,
 * completion ratios, pending grading queue, and a class-readiness summary.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import LoadingSkeleton from "../../components/LoadingSkeleton";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  GraduationCap, Users, BookOpen, ClipboardCheck, Target, ArrowRight,
  Sparkles,
} from "lucide-react";

export default function LMSTrainerDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/lms/trainer/dashboard");
        setData(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <DashboardLayout title="Trainer Console">
      <div data-testid="lmsx-trainer-dashboard" className="space-y-6 pb-12">
        <Hero
          eyebrow="Trainer · Diplomexa Academy"
          title="Train delegates to chair-grade readiness"
          subtitle="Track your courses, learners and class readiness in one premium console."
        />
        {loading || !data ? (
          <Loading />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Kpi icon={BookOpen} label="Courses" value={data.courses?.length ?? 0} testid="lmsx-trainer-courses" />
              <Kpi icon={Users} label="Learners" value={data.learners_total ?? 0} testid="lmsx-trainer-learners" />
              <Kpi icon={Target} label="Completions" value={data.completions_total ?? 0} testid="lmsx-trainer-completions" />
              <Kpi icon={ClipboardCheck} label="Pending grading" value={data.pending_grading ?? 0} testid="lmsx-trainer-pending" />
            </div>

            <section
              data-testid="lmsx-trainer-class-readiness"
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
                <Target size={14} className="text-[#D4AF37]" /> Class readiness
              </h2>
              <div className="mt-3 space-y-2">
                {(data.class_readiness || []).length === 0 ? (
                  <EmptyState label="No assigned courses yet" />
                ) : (
                  data.class_readiness.map((c) => (
                    <div key={c.course_id}
                          className="rounded-lg border border-slate-200 p-3 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-[#0A192F]">{c.course_id}</p>
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          {c.completed}/{c.enrolled} completed
                        </p>
                      </div>
                      <div className="w-32">
                        <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-[#0F4C81] to-[#D4AF37]"
                                style={{ width: `${c.completion_pct || 0}%` }} />
                        </div>
                        <p className="mt-1 text-right text-[10px] text-slate-500">{c.completion_pct}%</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section
              data-testid="lmsx-trainer-courses-list"
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
                  <BookOpen size={14} className="text-[#D4AF37]" /> Your courses
                </h2>
                <Link
                  to="/dashboard/lms/admin/course-builder"
                  data-testid="lmsx-trainer-new-course"
                  className="inline-flex items-center gap-1 rounded-lg border border-[#0F4C81]/40 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#0F4C81] hover:bg-blue-50"
                >
                  Open course builder <ArrowRight size={12} />
                </Link>
              </div>
              <div className="mt-3 space-y-2">
                {(data.courses || []).length === 0 ? (
                  <EmptyState label="You haven't authored a course yet. Open the Course Builder to start." />
                ) : (
                  data.courses.map(c => (
                    <div key={c.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 hover:border-[#D4AF37]/60">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#0A192F] truncate">{c.title}</p>
                        <p className="mt-0.5 text-[11px] text-slate-500 capitalize">{c.level} · {c.is_published ? "Published" : "Draft"}</p>
                      </div>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${c.is_published ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {c.is_published ? "Live" : "Draft"}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

// Shared mini UI for role dashboards
export function Hero({ eyebrow, title, subtitle }) {
  return (
    <section
      data-testid="lmsx-role-hero"
      className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-7 shadow-2xl text-white"
    >
      <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
      <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-[#0F4C81]/40 blur-3xl" />
      <div className="relative">
        <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
          <GraduationCap size={12} /> {eyebrow}
        </p>
        <h1 className="mt-1 text-2xl md:text-3xl font-bold leading-tight">{title}</h1>
        <p className="mt-2 max-w-xl text-sm text-blue-100">{subtitle}</p>
      </div>
    </section>
  );
}

export function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-sm border border-slate-200 bg-white p-4 shadow-sm hover:border-brand-gold transition-colors">
      <div className="flex items-center gap-2 eyebrow text-slate-500">
        <Icon size={12} className="text-brand-gold" /> {label}
      </div>
      <div className="mt-2 font-display text-2xl text-brand-navy-dark tabular-nums">{value}</div>
    </div>
  );
}

export function Loading() {
  return (
    <div className="grid h-72 place-items-center"><LoadingSkeleton variant="page" label="Loading trainer console…" /></div>
  );
}

export function EmptyState({ label, help, icon: Icon }) {
  const FallbackIcon = Icon || (() => (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
      <path d="M12 3v2M3 12h2M12 21v-2M21 12h-2M5.6 5.6l1.4 1.4M16.95 16.95l1.4 1.4M5.6 18.4l1.4-1.4M16.95 7.05l1.4-1.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ));
  return (
    <div className="text-center py-10 px-4 border border-dashed border-slate-300 rounded-sm bg-white">
      <div className="relative inline-flex items-center justify-center mb-4">
        <div aria-hidden className="absolute inset-0 rounded-full bg-brand-gold/15 blur-2xl animate-gold-pulse" />
        <div className="relative h-11 w-11 rounded-sm bg-brand-navy-dark text-brand-gold flex items-center justify-center shadow-gold-soft">
          <FallbackIcon className="h-5 w-5" />
        </div>
      </div>
      <p className="eyebrow text-brand-gold-dark">Nothing here yet</p>
      <h3 className="mt-2 font-display text-lg sm:text-xl text-brand-navy-dark leading-tight max-w-md mx-auto">
        {label}
      </h3>
      {help ? (
        <p className="mt-2 text-xs text-slate-500 max-w-md mx-auto leading-relaxed">{help}</p>
      ) : null}
    </div>
  );
}
